type ConversionRecord = Record<string, number>;
type ConversionMap = Record<string, ConversionRecord>;

const lengthConversions = {
  // Base unit is meter
  meter: 1,
  kilometer: 1000,
  centimeter: 0.01,
  millimeter: 0.001,
  
  // Imperial units
  mile: 1609.344,
  yard: 0.9144,
  foot: 0.3048,
  inch: 0.0254,
  
  // Other units
  nautical_mile: 1852,
  light_year: 9.461e15,
  parsec: 3.086e16,
  astronomical_unit: 1.496e11
} as const;

const weightConversions: ConversionRecord = {
  ug: 0.000001,
  mg: 0.001,
  cg: 0.01,
  dg: 0.1,
  g: 1,
  kg: 1000,
  mt: 1000000,
  oz: 28.3495,
  dr: 1.7718,
  lb: 453.592,
  st: 6350.29,
  cwt: 50802.3,
  ton_us: 907185,
  ton_uk: 1016047
};

const volumeConversions: ConversionRecord = {
  ul: 0.001,
  ml: 1,
  cl: 10,
  dl: 100,
  l: 1000,
  dash: 0.616115,
  pinch: 0.308057,
  tsp: 4.92892,
  tbsp: 14.7868,
  fl_oz: 29.5735,
  cup: 236.588,
  pt: 473.176,
  qt: 946.353,
  gal: 3785.41,
  bbl: 158987
};

const devConversions: ConversionRecord = {
  px: 1,           // base unit (pixel)
  rem: 16,         // 1rem = 16px (default)
  em: 16,          // 1em = 16px (default)
  pt: 1.333333,    // 1pt = 1.333333px
  pc: 16           // 1pc = 16px
};

const timeConversions: ConversionRecord = {
  ns: 1e-9,
  us: 1e-6,
  ms: 0.001,
  s: 1,
  min: 60,
  h: 3600,
  d: 86400,
  w: 604800,
  fortnight: 1209600,
  mo: 2592000,
  y: 31536000,
  decade: 315360000,
  century: 3153600000,
  millennium: 31536000000
};

const dataConversions: ConversionRecord = {
  bit: 0.125,
  nibble: 0.5,
  byte: 1,
  word: 2,
  kb: 1024,
  mb: 1048576,
  gb: 1073741824,
  tb: 1099511627776,
  pb: 1125899906842624,
  eb: 1152921504606847000,
  zb: 1.18e+21,
  yb: 1.21e+24
};

const energyConversions: ConversionRecord = {
  j: 1,
  kj: 1000,
  mj: 1000000,
  cal: 4.184,
  kcal: 4184,
  wh: 3600,
  kwh: 3600000,
  mwh: 3600000000,
  btu: 1055.06,
  therm: 105506000,
  ft_lb: 1.35582,
  ev: 1.602e-19
};

const pressureConversions: ConversionRecord = {
  pa: 1,
  kpa: 1000,
  mpa: 1000000,
  bar: 100000,
  psi: 6894.76,
  atm: 101325,
  mmhg: 133.322,
  inhg: 3386.39,
  torr: 133.322,
  hpa: 100
};

const speedConversions: ConversionRecord = {
  mps: 1,
  kph: 0.277778,
  mph: 0.44704,
  fps: 0.3048,
  knot: 0.514444,
  mach: 340.29,
  c: 299792458,
  kmh: 0.277778,
  fpm: 0.00508
};

const angleConversions: ConversionRecord = {
  deg: 1,
  rad: 57.2958,
  grad: 0.9,
  arcmin: 0.0166667,
  arcsec: 0.000277778,
  turn: 360,
  mil: 0.05625,
  circle: 360
};

const frequencyConversions: ConversionRecord = {
  hz: 1,
  khz: 1000,
  mhz: 1000000,
  ghz: 1000000000,
  thz: 1e12,
  rpm: 0.0166667,
  bpm: 0.0166667,
  cps: 1,
  rps: 1
};

const fuelConversions: ConversionRecord = {
  mpg_us: 1,
  mpg_uk: 1.20095,
  lp100km: 235.215,
  kml: 2.35215,
  gpm: 1,
  lpk: 0.1,
  mpk: 1.60934
};

const typographyConversions: ConversionRecord = {
  pt: 1,
  pc: 12,
  px: 0.75,
  in_type: 72,
  mm_type: 2.83465,
  q: 0.709,
  twips: 0.05,
  dtp: 1
};

const photographyConversions: ConversionRecord = {
  // Exposure Value (EV)
  ev: 1,
  
  // ISO/ASA Speed
  iso_100: 1,
  iso_200: 2,
  iso_400: 4,
  iso_800: 8,
  iso_1600: 16,
  iso_3200: 32,
  
  // F-stops (aperture)
  f1: 1,
  f1_4: 2,
  f2: 4,
  f2_8: 8,
  f4: 16,
  f5_6: 32,
  f8: 64,
  f11: 128,
  f16: 256,
  
  // Shutter Speed (in seconds)
  sec_1: 1,
  sec_1_2: 0.5,
  sec_1_4: 0.25,
  sec_1_8: 0.125,
  sec_1_15: 0.0667,
  sec_1_30: 0.0333,
  sec_1_60: 0.0167,
  sec_1_125: 0.008,
  sec_1_250: 0.004,
  sec_1_500: 0.002,
  sec_1_1000: 0.001
};

const torqueConversions: ConversionRecord = {
  nm: 1,
  ftlb: 1.35582,
  kgfm: 9.80665,
  inlb: 0.112985,
  ozin: 0.00706155,
  dyn_cm: 0.0000001,
  joule_rad: 1
};

const radiationConversions: ConversionRecord = {
  sv: 1,
  msv: 0.001,
  usv: 0.000001,
  rem: 0.01,
  rad: 0.01,
  gy: 1
};

const densityConversions: ConversionRecord = {
  kgm3: 1,
  gcm3: 1000,
  lbft3: 16.0185,
  ozin3: 1729.994,
  slugft3: 515.379,
  lbyd3: 0.593276,
  mgml: 1
};

const flowConversions: ConversionRecord = {
  m3s: 1,
  lps: 0.001,
  cfm: 0.000471947,
  gpm: 0.0000630902,
  lpm: 0.0000167,
  cfs: 0.0283168,
  gph: 0.00000105,
  afh: 0.000342
};

const soundConversions: ConversionRecord = {
  // Sound Pressure Level (SPL)
  db_spl: 1,                // Base unit (decibel SPL)
  db_a: 1,                  // A-weighted decibels
  db_c: 1,                  // C-weighted decibels
  
  // Sound Power
  db_w: 10,                 // Sound power level
  db_wm2: 1,               // Sound intensity level
  
  // Acoustic Intensity
  w_m2: 1e-12,             // Watts per square meter (reference)
  mw_m2: 1e-15,            // Milliwatts per square meter
  
  // Acoustic Pressure
  pa: 2e-5,                // Pascal (reference pressure)
  upa: 2e-11,              // Micropascal
  
  // Loudness
  phon: 1,                 // Perceived loudness
  sone: 2                  // Loudness level (1 sone = 40 phons)
};

const textileConversions: ConversionRecord = {
  denier: 1,
  tex: 0.111111,
  dtex: 1.11111,
  jute: 0.367437,
  cotton: 0.590541,
  worsted: 0.827757
};

const astronomyConversions: ConversionRecord = {
  au: 1,
  ly: 63241.1,
  pc: 206265,
  kpc: 206265000,
  mpc: 206265000000
};

const surveyingConversions: ConversionRecord = {
  chain: 1,
  rod: 0.25,
  link: 0.01,
  furlong: 10,
  acre: 10,
  hectare: 24.71
};

const viscosityConversions: ConversionRecord = {
  poise: 1,
  stokes: 100,
  pas: 0.1,
  cst: 1,
  mpa_s: 0.001,
  nsm2: 0.1,
  reyn: 6894.76,
  saybolt: 0.0021
};

const magneticConversions: ConversionRecord = {
  tesla: 1,
  gauss: 0.0001,
  weber: 1,
  maxwell: 0.00000001,
  mx: 0.00000001,
  unit_pole: 1.256637e-7,
  gamma: 0.0000001,
  emu: 0.0001
};

const lumberConversions: ConversionRecord = {
  fbm: 1,
  cord: 1000,
  ccf: 833.33,
  mbf: 1000,
  cunits: 100,
  tons: 2000,
  loads: 500,
  stacks: 250
};

const gardeningConversions: ConversionRecord = {
  // Area measurements
  sq_ft: 1,              // Base unit (square feet)
  sq_m: 10.764,          // Square meters
  acre: 43560,           // Acres
  hectare: 107639,       // Hectares
  
  // Plant spacing
  plants_per_ft: 1,      // Plants per square foot
  plants_per_m: 10.764,  // Plants per square meter
  row_ft: 1,             // Row spacing in feet
  row_m: 3.281,          // Row spacing in meters
  
  // Seeding rates
  seeds_per_lb: 1,       // Seeds per pound
  seeds_per_kg: 2.205,   // Seeds per kilogram
  lbs_per_acre: 43560,   // Pounds per acre
  kg_per_hectare: 107639 // Kilograms per hectare
};

const brewingConversions: ConversionRecord = {
  // Specific Gravity
  sg: 1,                    // Base unit
  plato: 4,                 // 1°P ≈ 4 points SG (1.004)
  brix: 4,                  // Similar to Plato
  
  // Color
  srm: 1,                   // Standard Reference Method
  ebc: 1.97,                // European Brewery Convention (1 SRM = 1.97 EBC)
  lovibond: 1.35,           // °L (1 SRM ≈ 1.35 °L)
  
  // Bitterness
  ibu: 1,                   // International Bitterness Units
  
  // Alcohol
  abv: 0.01,               // Alcohol By Volume (as decimal)
  abw: 0.008,              // Alcohol By Weight
  
  // Temperature
  fahrenheit: 1,
  celsius: 0.555,          // (°F - 32) × 5/9
  
  // Pressure
  psi: 1,
  bar: 14.504,            // 1 bar = 14.504 psi
  kpa: 0.145038           // 1 kPa = 0.145038 psi
};

const shoeConversions: ConversionRecord = {
  us_men: 1,
  us_women: 1.5,
  uk: 0.5,
  eu: 33,
  mondopoint: 0.25,
  cm: 0.25
};

const maritimeConversions: ConversionRecord = {
  fathom: 1,
  cable: 100,
  nmile: 1000,
  league: 3000,
  beaufort: 1
};

// Update the electronics conversions in both conversionFactors and conversions objects
const electronicsConversions: ConversionRecord = {
  // Voltage
  volt: 1,
  kilovolt: 1000,
  millivolt: 0.001,
  microvolt: 0.000001,
  
  // Current
  ampere: 1,
  milliamp: 0.001,
  microamp: 0.000001,
  kiloamp: 1000,
  
  // Resistance
  ohm: 1,
  kiloohm: 1000,
  megaohm: 1000000,
  milliohm: 0.001,
  
  // Power
  watt: 1,
  kilowatt: 1000,
  megawatt: 1000000,
  milliwatt: 0.001,
  
  // Capacitance
  farad: 1,
  microfarad: 0.000001,
  nanofarad: 1e-9,
  picofarad: 1e-12,
  
  // Inductance
  henry: 1,
  millihenry: 0.001,
  microhenry: 0.000001,
  
  // Charge
  coulomb: 1,
  millicoulomb: 0.001,
  microcoulomb: 0.000001,
  
  // Conductance
  siemens: 1,
  millisiemens: 0.001,
  microsiemens: 0.000001
};

// Update nuclear conversions to only include well-defined relationships
const nuclearConversions: ConversionRecord = {
  becquerel: 1,              // Base unit (Bq)
  kilobecquerel: 1000,       // kBq
  megabecquerel: 1e6,        // MBq
  gigabecquerel: 1e9,        // GBq
  curie: 3.7e10,             // Ci (1 Ci = 3.7×10¹⁰ Bq)
  millicurie: 3.7e7,         // mCi
  microcurie: 3.7e4          // µCi
};

// Update both conversionFactors and conversions objects
const conversionFactors: ConversionMap = {
  length: lengthConversions,
  weight: weightConversions,
  temperature: {
    celsius: 1,
    fahrenheit: 1,
    kelvin: 1
  },
  cooking: volumeConversions,
  dev: devConversions,
  time: timeConversions,
  data: dataConversions,
  energy: energyConversions,
  pressure: pressureConversions,
  speed: speedConversions,
  angle: angleConversions,
  frequency: frequencyConversions,
  fuel: fuelConversions,
  typography: typographyConversions,
  photography: photographyConversions,
  torque: torqueConversions,
  radiation: radiationConversions,
  density: densityConversions,
  flow: flowConversions,
  sound: soundConversions,
  textile: textileConversions,
  astronomy: astronomyConversions,
  surveying: surveyingConversions,
  viscosity: viscosityConversions,
  magnetic: magneticConversions,
  lumber: lumberConversions,
  gardening: gardeningConversions,
  brewing: brewingConversions,
  shoe: shoeConversions,
  maritime: maritimeConversions,
  electronics: electronicsConversions,
  nuclear: nuclearConversions,
  quantum: {
    planck_length: 1,
    planck_mass: 2.176434e-8,
    planck_time: 5.391247e-44,
    planck_temperature: 1.416784e32,
    planck_energy: 1.956e9
  },
  optics: {
    lumen: 1,
    lux: 1,
    candela: 1,
    nit: 1,
    lambert: 3183.099
  },
  acoustics: {
    spl: 1,
    sil: 1,
    sonority: 1,
    phon_acoustic: 1,
    sone: 1
  },
  magnetism: {
    tesla_mag: 1,
    gauss_mag: 1e-4,
    oersted: 79.5775,
    maxwell_mag: 1e-8,
    weber_mag: 1
  },
  cosmology: {
    hubble: 1,
    solar_mass: 1.989e30,
    redshift: 1,
    cosmic_year: 3.156e7,
    parsec_cosmo: 3.086e16
  },
  biology: {
    metabolic_rate: 1,
    blood_glucose: 1,
    enzyme_activity: 1,
    cell_count: 1,
    osmolarity: 1
  },
  networking: {
    bandwidth: 1,
    latency: 1,
    packet_loss: 1,
    throughput: 1,
    jitter: 1
  },
  digital_storage: {
    sector: 512,
    cluster: 4096,
    block: 512,
    page: 4096,
    inode: 128
  },
  computer_memory: {
    register: 64,
    cache_line: 64,
    stack_frame: 256,
    heap_block: 4096,
    virtual_page: 4096
  },
  'ai-ml': {
    // Base unit is FLOPS
    flops: 1,
    gflops: 1e9,
    tflops: 1e12,
    pflops: 1e15,
    // Parameters (assuming FP32)
    parameters: 1,
    parameters_b: 1e9,
    model_mb: 4 * 1024 * 1024, // 4 bytes per parameter for FP32
    model_gb: 4 * 1024 * 1024 * 1024,
    // VRAM and inference
    vram_gb: 1024 * 1024 * 1024,
    inference_ms: 1,
    inference_fps: 1000, // 1000ms = 1s
    tokens_s: 1
  }
} as const;

  const conversions = {
    length: lengthConversions,
    weight: weightConversions,
  temperature: {
    celsius: 1,
    fahrenheit: 1,
    kelvin: 1
  },
    cooking: volumeConversions,
    dev: devConversions,
    time: timeConversions,
    data: dataConversions,
    energy: energyConversions,
    pressure: pressureConversions,
    speed: speedConversions,
    angle: angleConversions,
    frequency: frequencyConversions,
    fuel: fuelConversions,
    typography: typographyConversions,
    photography: photographyConversions,
    torque: torqueConversions,
    radiation: radiationConversions,
    density: densityConversions,
    flow: flowConversions,
    sound: soundConversions,
    textile: textileConversions,
    astronomy: astronomyConversions,
    surveying: surveyingConversions,
    viscosity: viscosityConversions,
    magnetic: magneticConversions,
    lumber: lumberConversions,
    gardening: gardeningConversions,
    brewing: brewingConversions,
    shoe: shoeConversions,
    maritime: maritimeConversions,
  electronics: electronicsConversions,
  nuclear: nuclearConversions,
    quantum: {
      planck_length: 1,
      planck_mass: 2.176434e-8,
      planck_time: 5.391247e-44,
      planck_temperature: 1.416784e32,
      planck_energy: 1.956e9
    },
    optics: {
      lumen: 1,
      lux: 1,
      candela: 1,
      nit: 1,
      lambert: 3183.099
    },
    acoustics: {
      spl: 1,
      sil: 1,
      sonority: 1,
      phon_acoustic: 1,
      sone: 1
    },
    magnetism: {
      tesla_mag: 1,
      gauss_mag: 1e-4,
      oersted: 79.5775,
      maxwell_mag: 1e-8,
      weber_mag: 1
    },
    cosmology: {
      hubble: 1,
      solar_mass: 1.989e30,
      redshift: 1,
      cosmic_year: 3.156e7,
      parsec_cosmo: 3.086e16
    },
    biology: {
      metabolic_rate: 1,
      blood_glucose: 1,
      enzyme_activity: 1,
      cell_count: 1,
      osmolarity: 1
    },
    networking: {
      bandwidth: 1,
      latency: 1,
      packet_loss: 1,
      throughput: 1,
      jitter: 1
    },
    digital_storage: {
      sector: 512,
      cluster: 4096,
      block: 512,
      page: 4096,
      inode: 128
    },
    computer_memory: {
      register: 64,
      cache_line: 64,
      stack_frame: 256,
      heap_block: 4096,
      virtual_page: 4096
  },
  'ai-ml': {
    // Base unit is FLOPS
    flops: 1,
    gflops: 1e9,
    tflops: 1e12,
    pflops: 1e15,
    // Parameters (assuming FP32)
    parameters: 1,
    parameters_b: 1e9,
    model_mb: 4 * 1024 * 1024, // 4 bytes per parameter for FP32
    model_gb: 4 * 1024 * 1024 * 1024,
    // VRAM and inference
    vram_gb: 1024 * 1024 * 1024,
    inference_ms: 1,
    inference_fps: 1000, // 1000ms = 1s
    tokens_s: 1
  }
} as const;

export const convertValue = (
  value: number,
  fromUnit: string,
  toUnit: string,
  category: string
): number => {
  if (category === 'temperature') {
    return convertTemperature(value, fromUnit, toUnit);
  }

  const converted = conversions[category as keyof typeof conversions];
  if (!converted) return value;

  // Convert from source to base unit, then to target unit
  const fromFactor = converted[fromUnit as keyof typeof converted] || 1;
  const toFactor = converted[toUnit as keyof typeof converted] || 1;
  
  // For most units: first multiply by fromFactor to get to base unit,
  // then divide by toFactor to get to target unit
  return (value * fromFactor) / toFactor;
};

function convertTemperature(value: number, fromUnit: string, toUnit: string): number {
  // First convert to Kelvin as base unit
  let kelvin: number;
  
  switch (fromUnit) {
    case 'celsius':
      kelvin = value + 273.15;
      break;
    case 'fahrenheit':
      kelvin = (value - 32) * 5/9 + 273.15;
      break;
    case 'kelvin':
      kelvin = value;
      break;
    default:
      return value;
  }

  // Then convert from Kelvin to target unit
  switch (toUnit) {
    case 'celsius':
      return kelvin - 273.15;
    case 'fahrenheit':
      return (kelvin - 273.15) * 9/5 + 32;
    case 'kelvin':
      return kelvin;
    default:
      return value;
  }
}

export const getConversionSteps = (value: number, fromUnit: string, toUnit: string, category: string): string[] => {
  const steps: string[] = [];
  
  if (category === 'temperature') {
    if (fromUnit === 'celsius' && toUnit === 'fahrenheit') {
      const step1 = value * (9/5);
      const step2 = step1 + 32;
      steps.push(`Multiply ${value}°C by 9/5: ${step1}`);
      steps.push(`Add 32: ${step2}°F`);
    } else if (fromUnit === 'fahrenheit' && toUnit === 'celsius') {
      const step1 = value - 32;
      const step2 = step1 * (5/9);
      steps.push(`Subtract 32 from ${value}°F: ${step1}`);
      steps.push(`Multiply by 5/9: ${step2}°C`);
    }
    return steps;
  }

  const converted = conversions[category as keyof typeof conversions];
  if (!converted) return steps;

  const fromFactor = converted[fromUnit as keyof typeof converted] || 1;
  const toFactor = converted[toUnit as keyof typeof converted] || 1;

  // Show conversion to base unit
  const baseValue = value * fromFactor;
  steps.push(`Convert ${value} ${fromUnit} to base unit: ${value} × ${fromFactor} = ${baseValue}`);

  // Show conversion to target unit
  const result = baseValue / toFactor;
  steps.push(`Convert base unit to ${toUnit}: ${baseValue} ÷ ${toFactor} = ${result}`);

  return steps;
};

export const getConversionFormula = (fromUnit: string, toUnit: string, category: string): string => {
  // Special cases
  if (category === 'temperature') {
    if (fromUnit === 'celsius' && toUnit === 'fahrenheit') {
      return '°F = (°C × 9/5) + 32';
    } else if (fromUnit === 'fahrenheit' && toUnit === 'celsius') {
      return '°C = (°F - 32) × 5/9';
    } else if (fromUnit === 'celsius' && toUnit === 'kelvin') {
      return 'K = °C + 273.15';
    } else if (fromUnit === 'kelvin' && toUnit === 'celsius') {
      return '°C = K - 273.15';
    } else if (fromUnit === 'fahrenheit' && toUnit === 'kelvin') {
      return 'K = (°F - 32) × 5/9 + 273.15';
    } else if (fromUnit === 'kelvin' && toUnit === 'fahrenheit') {
      return '°F = (K - 273.15) × 9/5 + 32';
    }
  }

  const converted = conversions[category as keyof typeof conversions];
  if (!converted) return '';

  const fromFactor = converted[fromUnit as keyof typeof converted] || 1;
  const toFactor = converted[toUnit as keyof typeof converted] || 1;

  // If both factors are 1, show direct equality
  if (fromFactor === 1 && toFactor === 1) {
    return `${toUnit} = ${fromUnit}`;
  }

  // If converting to a larger unit (division)
  if (fromFactor < toFactor) {
    return `${toUnit} = ${fromUnit} ÷ ${toFactor/fromFactor}`;
  }

  // If converting to a smaller unit (multiplication)
  if (fromFactor > toFactor) {
    return `${toUnit} = ${fromUnit} × ${fromFactor/toFactor}`;
  }

  // Default case with explicit factors
  return `${toUnit} = ${fromUnit} × ${fromFactor} ÷ ${toFactor}`;
};